import { useSeoMeta, useHead } from '@unhead/react';
import { Link } from 'react-router-dom';
import {Calendar, Clock, Tag} from 'lucide-react';
import { SiteShell } from '@/components/SiteShell';
import { PageHero } from '@/components/PageHero';
import { normalizeCanonicalUrl } from '@/lib/seo';
import { blogPosts } from '@/lib/blogPosts';
import blogBg from '../../images/VIP House Movers Removal Boxes.jpg';

export default function Blog() {
  const [featured, ...rest] = blogPosts;

  const categories = Array.from(new Set(blogPosts.map((p) => p.category)));

  useSeoMeta({
    title: 'Moving Tips & Guides | Removals Advice Wolverhampton | VIP House Movers',
    description: 'Expert UK moving tips, checklists, packing guides and house removals advice from VIP House Movers Wolverhampton. Read guides for stress-free moves and moving services.',
    ogTitle: 'Moving Tips & Guides | VIP House Movers Blog',
    ogDescription: 'UK moving tips, packing guides, and removal checklists from VIP House Movers Wolverhampton. Free advice for stress-free moves.',
    ogImage: 'https://www.viphousemovers.com/og-home.jpg',
    ogUrl: 'https://www.viphousemovers.com/blog',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Moving Tips & Guides | VIP House Movers Blog',
    twitterDescription: 'Expert UK moving tips and removal guides from VIP House Movers Wolverhampton.',
    twitterImage: 'https://www.viphousemovers.com/og-home.jpg',
  });
  const canonicalHref = normalizeCanonicalUrl('https://www.viphousemovers.com/blog');

  useHead({
    link: [{ rel: 'canonical', href: canonicalHref }],
    meta: [
      {
        name: 'keywords',
        content: 'moving tips, removals advice, packing guides, house removal blogs, moving checklists, UK removal tips, Wolverhampton moving advice, local moving guides',
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.viphousemovers.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: 'https://www.viphousemovers.com/blog',
            },
          ],
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'VIP House Movers Blog',
          description: 'Expert moving tips, guides, and removal advice from VIP House Movers.',
          url: 'https://www.viphousemovers.com/blog',
          publisher: {
            '@type': 'Organization',
            name: 'VIP House Movers',
            url: 'https://www.viphousemovers.com',
          },
        }),
      },
    ],
  });

  return (
    <SiteShell activePath="/blog" fixedNav>
      <PageHero
        eyebrow="VIP House Movers Blog"
        title="Moving tips, guides, and UK removals expertise"
        description="Expert advice from the VIP House Movers team on stress-free moves, packing, pricing, checklists and settling into a new home across the Midlands."
        align="center"
        backgroundImage={blogBg}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Category chips */}
        <div className="mb-12 flex flex-wrap gap-2 justify-center">
          {categories.map((cat) => (
            <span
              key={cat}
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#8a7118] bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full px-4 py-1.5"
            >
              {cat}
            </span>
          ))}
        </div>

        {/* Featured post */}
        {featured ? (
          <Link
            to={featured.path}
            className="group block mb-14 rounded-3xl overflow-hidden border border-gray-200 bg-white shadow-sm hover:shadow-xl transition-shadow"
          >
            <div className="grid md:grid-cols-2">
              <div className="h-64 md:h-full overflow-hidden">
                <img
                  src={featured.heroImage}
                  alt={featured.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">
                  Featured Article • {featured.category}
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight group-hover:text-[#D4AF37] transition-colors">
                  {featured.title}
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">{featured.excerpt}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-[#D4AF37]" />
                    {featured.date}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-[#D4AF37]" />
                    {featured.readTime}
                  </span>
                </div>
                <span className="mt-6 inline-flex items-center gap-2 text-[#D4AF37] font-bold border-b-2 border-[#D4AF37] self-start pb-0.5 group-hover:gap-3 transition-all">
                  Read the full guide &rarr;
                </span>
              </div>
            </div>
          </Link>
        ) : null}

        {/* Article grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rest.map((post) => (
            <Link
              key={post.slug}
              to={post.path}
              className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow flex flex-col"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src={post.heroImage}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-[#D4AF37] mb-3">
                  <span className="inline-flex items-center gap-1">
                    <Tag className="w-3.5 h-3.5" />
                    {post.category}
                  </span>
                  <span className="text-gray-300">•</span>
                  <span>{post.readTime}</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-3 leading-snug group-hover:text-[#D4AF37] transition-colors">
                  {post.title}
                </h2>
                <p className="text-gray-600 mb-5 line-clamp-3 flex-1">{post.excerpt}</p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-xs text-gray-500 inline-flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {post.date}
                  </span>
                  <span className="text-gray-900 font-bold border-b-2 border-[#D4AF37] text-sm">
                    Read More
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Bottom CTA */}
        <section className="mt-20 bg-[#111827] text-white rounded-2xl p-8 md:p-12 text-center">
          <p className="text-[#D4AF37] font-semibold uppercase tracking-wider text-sm mb-2">
            VIP House Movers
          </p>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to plan your next move?
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-6">
            Whether it is a studio flat in Wolverhampton or a 5-bed family home in Staffordshire, our expert team
            is ready to help with a calm, organised, fully insured move.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/quote"
              className="bg-[#D4AF37] text-[#111827] font-semibold px-6 py-3 rounded-lg hover:bg-[#c9a332] transition-colors"
            >
              Get a Free Quote
            </Link>
            <Link
              to="/contact"
              className="border border-white/30 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/10 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </section>
      </div>
    </SiteShell>
  );
}

